/**
 * Live IVR call state for the logged-in agent.
 * Polls /activeCalls ONLY while a call is in progress — not continuously when idle.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { getCallParams, makeCall } from "./service";
import urls from "../global/constants/UrlConstants";
import { store } from "./store";

export type ActiveIvrCall = {
  _id: string;
  status: string;
  agentName?: string;
  agentUserId?: string;
  agentCrmUserId?: string;
  customerNumber?: string;
  leadName?: string;
  leadId?: string;
  durationFormatted?: string;
  durationSeconds?: number;
  startedAt?: string;
  answeredAt?: string;
  uniqueCallId?: string;
  referenceId?: string;
  lastEventType?: string;
};

let listeners = new Set<(calls: ActiveIvrCall[]) => void>();
let cached: ActiveIvrCall[] = [];
let pollTimer: ReturnType<typeof setTimeout> | null = null;
let inFlight = false;
let lastNonEmptyAt = 0;
let pollingEnabled = false;
const EMPTY_GRACE_MS = 8_000;
const LIVE_MS = 3_000;

function notify() {
  listeners.forEach((fn) => fn(cached));
}

function clearPoll() {
  if (pollTimer) {
    clearTimeout(pollTimer);
    pollTimer = null;
  }
}

function stopPolling() {
  pollingEnabled = false;
  clearPoll();
}

function scheduleNext() {
  clearPoll();
  if (!pollingEnabled || listeners.size === 0) return;
  // Only keep polling while we believe a call is live
  if (cached.length === 0 && Date.now() - lastNonEmptyAt > EMPTY_GRACE_MS) {
    stopPolling();
    return;
  }
  pollTimer = setTimeout(() => {
    void fetchActiveCalls().finally(() => scheduleNext());
  }, LIVE_MS);
}

/** Keep only calls belonging to the logged-in agent. */
function filterMine(rows: ActiveIvrCall[]): ActiveIvrCall[] {
  const auth = store.getState()?.auth || {};
  const uuid = String(auth.uuid || "").trim();
  const userId = String(auth.userId || "").trim();
  if (!uuid && !userId) return rows;
  return (rows || []).filter((row) => {
    const agentUuid = String(row.agentUserId || "").trim();
    const agentCrm = String(row.agentCrmUserId || "").trim();
    if (uuid && agentUuid && agentUuid === uuid) return true;
    if (userId && agentCrm && agentCrm === userId) return true;
    if (String(row._id || "").startsWith("local-")) return true;
    if (!agentUuid && !agentCrm) return true;
    return false;
  });
}

async function fetchActiveCalls(): Promise<ActiveIvrCall[]> {
  if (inFlight) return cached;
  inFlight = true;
  try {
    const params = await getCallParams("GET");
    if (params?.headers) {
      params.headers["Cache-Control"] = "no-cache";
      params.headers.Pragma = "no-cache";
    }
    const res = await makeCall(`${urls.ACTIVECALLS}?scope=mine`, params);
    if (res?.success === 1 && Array.isArray(res.data)) {
      const mine = filterMine(res.data);
      if (mine.length > 0) {
        cached = mine;
        lastNonEmptyAt = Date.now();
        pollingEnabled = true;
      } else if (
        cached.length > 0 &&
        Date.now() - lastNonEmptyAt < EMPTY_GRACE_MS
      ) {
        // brief grace after empty race
      } else {
        cached = [];
        stopPolling();
      }
    }
  } catch (_) {
    /* keep cache */
  } finally {
    inFlight = false;
  }
  notify();
  return cached;
}

/** Start live polling (after click2call / seed). No-op if already polling. */
function startLivePolling() {
  pollingEnabled = true;
  if (pollTimer) return;
  void fetchActiveCalls().finally(() => scheduleNext());
}

export function refreshActiveIvrCalls() {
  lastNonEmptyAt = 0;
  return fetchActiveCalls();
}

/**
 * Hide the Ongoing chip immediately.
 * Pass refetch=false while End API is in flight.
 */
export function clearActiveIvrCalls(options?: { refetch?: boolean }) {
  cached = [];
  lastNonEmptyAt = 0;
  stopPolling();
  notify();
  if (options?.refetch === false) return Promise.resolve([]);
  // One-shot confirm empty — does not restart idle polling
  return fetchActiveCalls().then((rows) => {
    if (!rows.length) stopPolling();
    return rows;
  });
}

/** Call after click2call so the chip appears and polling starts. */
export function seedActiveIvrCall(
  partial: Partial<ActiveIvrCall> & { _id?: string }
) {
  const auth = store.getState()?.auth || {};
  const row: ActiveIvrCall = {
    _id: String(partial._id || `local-${Date.now()}`),
    status: partial.status || "initiated",
    agentName: partial.agentName,
    agentUserId: partial.agentUserId || auth.uuid,
    agentCrmUserId: partial.agentCrmUserId || auth.userId,
    customerNumber: partial.customerNumber,
    leadName: partial.leadName,
    leadId: partial.leadId,
    durationFormatted: partial.durationFormatted || "0:00",
    durationSeconds: partial.durationSeconds || 0,
    startedAt: partial.startedAt || new Date().toISOString(),
    uniqueCallId: partial.uniqueCallId,
    referenceId: partial.referenceId,
  };
  cached = [row, ...cached.filter((c) => c._id !== row._id)];
  lastNonEmptyAt = Date.now();
  notify();
  startLivePolling();
}

export function useActiveIvrCalls() {
  const [calls, setCalls] = useState<ActiveIvrCall[]>(cached);

  useEffect(() => {
    const listener = (next: ActiveIvrCall[]) => setCalls(next);
    listeners.add(listener);
    setCalls(cached);
    // Do NOT start idle polling — only poll when a call is seeded / live
    return () => {
      listeners.delete(listener);
      if (listeners.size === 0) stopPolling();
    };
  }, []);

  const mine = useMemo(() => filterMine(calls), [calls]);
  const hasActiveCall = mine.length > 0;
  const primary = mine[0] || null;

  const reload = useCallback(() => {
    if (hasActiveCall || pollingEnabled) return fetchActiveCalls();
    return Promise.resolve(cached);
  }, [hasActiveCall]);

  return { calls: mine, primary, hasActiveCall, reload };
}

/** Admin Team panel: all org live calls (does not affect header chip cache). */
export async function fetchTeamActiveCalls(): Promise<ActiveIvrCall[]> {
  const params = await getCallParams("GET");
  if (params?.headers) {
    params.headers["Cache-Control"] = "no-cache";
    params.headers.Pragma = "no-cache";
  }
  const res = await makeCall(`${urls.ACTIVECALLS}?scope=team`, params);
  if (res?.success === 1 && Array.isArray(res.data)) return res.data;
  return [];
}
